import { Access, AccessDependency, AccessSource, EmployeeSubtype, EmployeeType, Prisma, User } from '@prisma/client';

// export type FindAccessParams = {
//   skip?: number;
//   take?: number;
//   cursor?: Prisma.AccessWhereUniqueInput;
//   where?: Prisma.AccessWhereInput;
//   orderBy?: Prisma.AccessOrderByWithRelationInput;
// };

export type FindAccessDependencyParams = {
  skip?: number;
  take?: number;
  cursor?: Prisma.AccessDependencyWhereUniqueInput;
  where?: Prisma.AccessDependencyWhereInput;
  orderBy?: Prisma.AccessDependencyOrderByWithRelationInput;
};

export type AccessDetail = Access & {
  parentDependencies: AccessDependency[];
  childDependencies: AccessDependency[];
  createdByUser: User;
  updatedByUser: User;
};

export type EnsureAccessDto = {
  externalId: string;
  accessSource: AccessSource;
};

export type CreateParentDependencyDto = {
  parent: EnsureAccessDto[];
};

export type ExceptionDto = {
  wwid: string;
  justification: string;
};

export type CreateAccessRuleDto = {
  employeeType: EmployeeType;
  employeeSubtype?: EmployeeSubtype;
  owners: string[];
  exceptions: ExceptionDto[];
};

export type PromoteAccessConflictDto = {
  access: EnsureAccessDto;
} & CreateAccessConflictDto;

export type PromoteDepartmentConflictDto = {
  access: EnsureAccessDto;
} & CreateDepartmentConflictDto;

export type CreateAccessConflictDto = CreateAccessRuleDto & {
  externalId: string;
  accessSource: AccessSource;
};

export type CreateDepartmentConflictDto = CreateAccessRuleDto & {
  departmentId: string;
};

export type CreateBulkAccessRuleDto = {
  employeeType: EmployeeType;
  employeeSubtype: EmployeeSubtype[];
  owners: string[];
};

export type CreateBulkAccessConflictDto = CreateBulkAccessRuleDto & {
  conflicts: EnsureAccessDto[];
};

export type CreateBulkDepartmentConflictDto = CreateBulkAccessRuleDto & {
  departments: string[];
};

/**
 * Any access provider should return at least these fields.
 */
export type SourceAccessItem = {
  name: string;
  externalId: string;
  accessSource: AccessSource;
  employeeType?: EmployeeType;
  description?: string;
};

export type UpdateAccessMetaDto = {
  id: number;
  isActive: boolean;
  meta?: AgsRoleMeta | AgsEntitlementMeta | IemGroupMeta;
};

// Use these to type the access object so we know what type the meta data is.

export const isAgsRoleAccess = (access: Access | SourceAccessItem): access is AgsRoleAccess => {
  return access.accessSource === AccessSource.AGS_ROLE;
};

export const isAgsEntitlementAccess = (access: Access | SourceAccessItem): access is AgsEntitlementAccess => {
  return access.accessSource === AccessSource.AGS_ENTITLEMENT;
};

export const isIemAccess = (access: Access | SourceAccessItem): access is IemAccess => {
  return access.accessSource === AccessSource.IEM;
};

export type AgsRoleAccess = Access & {
  meta?: AgsRoleMeta;
};

export type AgsEntitlementAccess = Access & {
  meta?: AgsEntitlementMeta;
};

export type IemAccess = Access & {
  meta?: IemGroupMeta;
};

export type AgsRoleMeta = {
  userType: string;
  disabled: boolean;
  displayName: string;
  created: string;
  lastModified: string;
  entitlements: AgsEntitlementMeta[];
};

export type AgsEntitlementMeta = {
  displayName: string;
  created: string;
  lastModified: string;
  fullName: string;
  application: string;
  accessType: string;
  schemas: string[];
  training?: AgsTrainingMeta;
  unixGroups: string[];
};

export type AgsTrainingMeta = {
  type: string;
  id: string;
  name: string;
};

export type IemGroupMeta = {
  objectSid: string;
  objectSidBase64: string;
  distinguishedName: string;
  commonName: string;
  description: string;
  displayName: string;
  whenCreated: string;
  memberOf: string[];
  unixGroups: string[];
};

export type SetAccessPullMembershipThresholdOneTimeOverrideApiArgs = {
  value: boolean;
};
